import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Search, Wallet, X, ChevronRight } from 'lucide-react';
import ManualWalletEntryDialog from './ManualWalletEntryDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  partner: 'Partner',
  bulk_bandwidth: 'Partner',
  iptv_distributor: 'Distributor',
  lco: 'LCO',
  iptv_lco: 'IPTV LCO',
};
const TYPE_COLOR = {
  partner: 'bg-purple-50 text-purple-700 border-purple-200',
  bulk_bandwidth: 'bg-purple-50 text-purple-700 border-purple-200',
  iptv_distributor: 'bg-amber-50 text-amber-700 border-amber-200',
  lco: 'bg-blue-50 text-blue-700 border-blue-200',
  iptv_lco: 'bg-blue-50 text-blue-700 border-blue-200',
};
const fmt = (n) => n == null || n === '' ? '—' : `Rs.${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function WalletBalances() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ totals: {}, rows: [] });
  const [filters, setFilters] = useState({ q: '', customer_type: '' });
  const [detailCust, setDetailCust] = useState(null); // customer row for detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.q) p.append('q', filters.q);
      if (filters.customer_type) p.append('customer_type', filters.customer_type);
      const res = await fetch(`${API}/api/admin/wallet-reconcile/balances?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error('Load failed');
        return;
      }
      const d = await res.json();
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetailCust(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/wallet-reconcile/balances/${row.customer_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDetail(await res.json());
      } else {
        toast.error('Detail load failed');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const T = data.totals || {};

  return (
    <div className="space-y-4" data-testid="wallet-balances-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-600" /> Wallet Balances
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Live balance for every Partner, Distributor and LCO across OTT, IPTV and Railtel wallets.
            Click a row for a full transaction breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ManualWalletEntryDialog token={token} onDone={load} />
          <Button variant="outline" onClick={load} disabled={loading} data-testid="wb-reload-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Customers" value={T.customer_count || 0} isNumber />
        <SummaryCard label="OTT Wallets" value={T.ott_total} color="text-indigo-700" />
        <SummaryCard label="IPTV Wallets" value={T.iptv_total} color="text-amber-700" />
        <SummaryCard label="Railtel Wallets" value={T.railtel_total} color="text-teal-700" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, ID, phone, email…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="pl-9"
              data-testid="wb-search"
            />
          </div>
          <Select
            value={filters.customer_type || 'all'}
            onValueChange={(v) => setFilters((f) => ({ ...f, customer_type: v === 'all' ? '' : v }))}
          >
            <SelectTrigger className="w-56" data-testid="wb-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="partner">Partner</SelectItem>
              <SelectItem value="iptv_distributor">Distributor</SelectItem>
              <SelectItem value="lco">LCO</SelectItem>
              <SelectItem value="iptv_lco">IPTV LCO</SelectItem>
            </SelectContent>
          </Select>
          {(filters.q || filters.customer_type) && (
            <Button variant="ghost" onClick={() => setFilters({ q: '', customer_type: '' })}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          <Table data-testid="wb-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">OTT</TableHead>
                <TableHead className="text-right">IPTV</TableHead>
                <TableHead className="text-right">Railtel</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">
                  <RefreshCw className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                </TableCell></TableRow>
              )}
              {!loading && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">
                  No matching partners / distributors / LCOs.
                </TableCell></TableRow>
              )}
              {data.rows.map((r) => {
                const total = r.ott_balance + r.iptv_balance + r.railtel_balance;
                const anyDrift = Math.abs(r.ott_drift) > 0.01 || Math.abs(r.iptv_drift) > 0.01 || Math.abs(r.railtel_drift) > 0.01;
                return (
                  <TableRow
                    key={r.customer_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openDetail(r)}
                    data-testid={`wb-row-${r.customer_id}`}
                  >
                    <TableCell>
                      <div className="font-medium text-slate-900" data-testid={`wb-name-${r.customer_id}`}>{r.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.customer_id}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TYPE_COLOR[r.customer_type] || ''}>
                        {TYPE_LABELS[r.customer_type] || r.customer_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.contact_phone && <div>{r.contact_phone}</div>}
                      {r.email && <div className="text-slate-500">{r.email}</div>}
                    </TableCell>
                    <BalanceCell balance={r.ott_balance} drift={r.ott_drift} />
                    <BalanceCell balance={r.iptv_balance} drift={r.iptv_drift} />
                    <BalanceCell balance={r.railtel_balance} drift={r.railtel_drift} />
                    <TableCell className={`text-right font-bold ${anyDrift ? 'text-amber-700' : 'text-slate-900'}`}>
                      {fmt(total)}
                    </TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-slate-400" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detailCust} onOpenChange={(o) => !o && setDetailCust(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailCust?.name}</DialogTitle>
            <DialogDescription>
              <span className="font-mono text-xs">{detailCust?.customer_id}</span>
              {detailCust && (
                <Badge variant="outline" className={`ml-2 ${TYPE_COLOR[detailCust.customer_type] || ''}`}>
                  {TYPE_LABELS[detailCust.customer_type] || detailCust.customer_type}
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-10 text-center"><RefreshCw className="h-6 w-6 animate-spin inline text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              {detail.wallets.map((w) => (
                <WalletDetail key={w.wallet_type} w={w} />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BalanceCell({ balance, drift }) {
  const hasDrift = Math.abs(drift) > 0.01;
  return (
    <TableCell className={`text-right whitespace-nowrap ${hasDrift ? 'text-amber-700 font-semibold' : ''}`}>
      {fmt(balance)}
      {hasDrift && (
        <div className="text-[10px] text-amber-600">
          drift {drift > 0 ? '+' : ''}{fmt(drift)}
        </div>
      )}
    </TableCell>
  );
}

function SummaryCard({ label, value, color, isNumber }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-bold mt-1 ${color || 'text-slate-900'}`}>
          {isNumber ? Number(value || 0).toLocaleString('en-IN') : fmt(value)}
        </div>
      </CardContent>
    </Card>
  );
}

function WalletDetail({ w }) {
  const WT_COLOR = {
    ott: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    iptv: 'bg-amber-50 text-amber-700 border-amber-200',
    railtel: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  const hasDrift = Math.abs(w.drift) > 0.01;
  return (
    <Card className={hasDrift ? 'border-amber-300' : ''}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Badge variant="outline" className={WT_COLOR[w.wallet_type]}>{w.wallet_type.toUpperCase()}</Badge>
            Wallet
          </CardTitle>
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-900">{fmt(w.balance)}</div>
            {hasDrift && <div className="text-xs text-amber-700">drift {w.drift > 0 ? '+' : ''}{fmt(w.drift)}</div>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <div><span className="text-slate-500">Credits: </span><span className="text-emerald-700 font-medium">{fmt(w.total_credits)}</span></div>
          <div><span className="text-slate-500">Debits: </span><span className="text-red-700 font-medium">{fmt(w.total_debits)}</span></div>
          <div><span className="text-slate-500">Expected: </span><span className="font-medium">{fmt(w.expected)}</span></div>
        </div>
        {w.recent_transactions.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No transactions yet.</p>
        ) : (
          <div className="border rounded-md max-h-64 overflow-y-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="h-8 text-xs">Date</TableHead>
                  <TableHead className="h-8 text-xs">Description</TableHead>
                  <TableHead className="h-8 text-xs text-right">Amount</TableHead>
                  <TableHead className="h-8 text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {w.recent_transactions.map((t) => (
                  <TableRow key={t.transaction_id} className="text-xs">
                    <TableCell className="whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <div>{t.description}</div>
                      {t.reference_id && <div className="text-slate-400 font-mono text-[10px]">{t.reference_id}</div>}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${t.type === 'credit' ? 'text-emerald-700' : 'text-red-700'}`}>
                      {t.type === 'credit' ? '+' : '-'}{fmt(t.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        t.status === 'success' ? 'text-emerald-700 border-emerald-200 bg-emerald-50 text-[10px]' :
                        t.status === 'failed' ? 'text-red-700 border-red-200 bg-red-50 text-[10px]' :
                        'text-slate-500 text-[10px]'
                      }>{t.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
